import React, { useMemo } from "react";
import { FilterX } from "lucide-react";
import {
  SCHEDULE_STATUSES,
  SCHEDULE_STATUS_LABELS,
} from "../../utils/examsschedule/examConstants";
import { toId } from "../../utils/examsschedule/examHelpers";
import { Card, CardContent, Select, Input, Button } from "../../components/ui";

/**
 * Shared filter bar for exam / schedule list pages.
 * All values are selects or dates — no free ObjectIds.
 */
export default function ExamFiltersBar({
  filters = {},
  onChange,
  classes = [],
  subjects = [],
  academicYears = [],
  examTypes = [],
  showStatus = true,
  showClass = true,
  showSubject = true,
  showAcademicYear = true,
  showExamType = false,
  showDateRange = false,
  disabled = false,
}) {
  const set = (patch) => onChange?.({ ...filters, ...patch });

  // Map options for UI Select components
  const academicYearOptions = useMemo(
    () => [
      { label: "All years", value: "" },
      ...academicYears.map((y) => {
        const label =
          typeof y === "string"
            ? y
            : y.yearName || y.name || y.academicYear || toId(y);
        const val =
          typeof y === "string"
            ? y
            : y.yearName || y.name || y.academicYear || toId(y);
        return { label, value: val };
      }),
    ],
    [academicYears]
  );

  const classOptions = useMemo(
    () => [
      { label: "All classes", value: "" },
      ...classes.map((c) => ({
        label: c.className || c.name,
        value: toId(c),
      })),
    ],
    [classes]
  );

  const subjectOptions = useMemo(
    () => [
      { label: "All subjects", value: "" },
      ...subjects.map((s) => ({
        label: s.name,
        value: toId(s),
      })),
    ],
    [subjects]
  );

  const statusOptions = useMemo(
    () => [
      { label: "All statuses", value: "" },
      ...SCHEDULE_STATUSES.map((st) => ({
        label: SCHEDULE_STATUS_LABELS[st] || st,
        value: st,
      })),
    ],
    []
  );

  const examTypeOptions = useMemo(
    () => [
      { label: "All types", value: "" },
      ...examTypes.map((t) => ({
        label: t.name || t.code,
        value: t.code || toId(t),
      })),
    ],
    [examTypes]
  );

  return (
    <Card className="mb-6">
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {showAcademicYear && (
            <div>
              <Select
                label="Academic year"
                options={academicYearOptions}
                value={filters.academicYear || ""}
                disabled={disabled}
                onChange={(val) => set({ academicYear: val })}
              />
            </div>
          )}

          {showClass && (
            <div>
              <Select
                label="Class"
                options={classOptions}
                value={filters.classId || ""}
                disabled={disabled}
                onChange={(val) => set({ classId: val })}
              />
            </div>
          )}

          {showSubject && (
            <div>
              <Select
                label="Subject"
                options={subjectOptions}
                value={filters.subjectId || ""}
                disabled={disabled}
                onChange={(val) => set({ subjectId: val })}
              />
            </div>
          )}

          {showStatus && (
            <div>
              <Select
                label="Schedule status"
                options={statusOptions}
                value={filters.scheduleStatus || ""}
                disabled={disabled}
                onChange={(val) => set({ scheduleStatus: val })}
              />
            </div>
          )}

          {showExamType && (
            <div>
              <Select
                label="Exam type"
                options={examTypeOptions}
                value={filters.type || filters.examTypeId || ""}
                disabled={disabled}
                onChange={(val) => {
                  const match = examTypes.find(
                    (t) => toId(t) === val || t.code === val
                  );
                  set({
                    type: match?.code || val,
                    examTypeId: match ? toId(match) : "",
                  });
                }}
              />
            </div>
          )}

          {showDateRange && (
            <>
              <div>
                <Input
                  label="From date"
                  type="date"
                  disabled={disabled}
                  value={filters.fromDate || ""}
                  onChange={(e) => set({ fromDate: e.target.value })}
                />
              </div>
              <div>
                <Input
                  label="To date"
                  type="date"
                  disabled={disabled}
                  value={filters.toDate || ""}
                  onChange={(e) => set({ toDate: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="sm:col-span-2 lg:col-span-4 flex justify-end pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              leftIcon={<FilterX className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />}
              onClick={() =>
                onChange?.({
                  academicYear: "",
                  classId: "",
                  subjectId: "",
                  scheduleStatus: "",
                  type: "",
                  examTypeId: "",
                  fromDate: "",
                  toDate: "",
                })
              }
              className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              Clear filters
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}